import type { DocumentContent, UserDocument } from '@/types/documents'
import type { ReaderBlock, ReaderContent } from '@/types/reader'
import { READER_PROCESSOR_VERSION } from './constants'
import { buildGlossaryFromTokens } from './glossary'
import {
  documentContentToText,
  splitParagraphs,
  splitSentences,
  cleanReaderText
} from './text'
import { tokenizeText, type DictionaryLookup } from './tokenizer'

function createBlockId(prefix: string, index: number) {
  return `${prefix}-${index + 1}`
}

export function documentTextToBlocks(
  text: string,
  lookup: DictionaryLookup = new Map()
): ReaderBlock[] {
  const cleanText = cleanReaderText(text)
  const blocks: ReaderBlock[] = []
  let sentenceIndex = 0

  for (const paragraph of splitParagraphs(cleanText)) {
    for (const sentence of splitSentences(paragraph)) {
      blocks.push({
        id: createBlockId('sentence', sentenceIndex),
        type: 'sentence',
        originalText: sentence,
        tokens: tokenizeText(sentence, lookup)
      })
      sentenceIndex += 1
    }
  }

  if (blocks.length === 0 && cleanText) {
    blocks.push({
      id: createBlockId('sentence', 0),
      type: 'sentence',
      originalText: cleanText,
      tokens: tokenizeText(cleanText, lookup)
    })
  }

  return blocks
}

export function documentContentToReaderContent(
  document: Pick<
    UserDocument,
    'id' | 'title' | 'file_name' | 'file_type' | 'created_at' | 'updated_at'
  > & { content: DocumentContent },
  lookup: DictionaryLookup = new Map()
): ReaderContent {
  const blocks = documentTextToBlocks(
    documentContentToText(document.content),
    lookup
  )
  const entriesById = new Map(
    [...lookup.values()].flat().map((entry) => [entry.id, entry] as const)
  )
  const tokens = blocks.flatMap((block) => block.tokens)

  return {
    id: document.id,
    sourceType: 'document',
    title: document.title,
    subtitle: document.file_name || undefined,
    metadata: {
      fileName: document.file_name || undefined,
      fileType: document.file_type,
      createdAt: document.created_at,
      updatedAt: document.updated_at,
      sourceDocumentId: document.id
    },
    blocks,
    glossary: buildGlossaryFromTokens(tokens, entriesById),
    processorVersion: READER_PROCESSOR_VERSION
  }
}

export function plainDocumentContentToReaderContent(
  id: string,
  title: string,
  content: DocumentContent,
  lookup: DictionaryLookup = new Map()
) {
  return documentContentToReaderContent(
    {
      id,
      title,
      content,
      file_name: null,
      file_type: 'text',
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    },
    lookup
  )
}
